import React, { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppSelector } from '../hooks';
import { authService } from '../services';
import { Button, Input } from '../components/common';
import './ChangePasswordPage.css';

export const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken } = useAppSelector((state) => state.auth);
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!currentPassword.trim()) {
      setError('Please enter your current password');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    if (!accessToken) {
      setError('You must be logged in to change your password');
      return;
    }

    setIsLoading(true);
    try {
      await authService.changePassword(accessToken, currentPassword, newPassword);
      setSuccessMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Navigate back to profile after a short delay
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (err: any) {
      if (err.name === 'NotAuthorizedException') {
        setError('Current password is incorrect');
      } else if (err.name === 'InvalidPasswordException') {
        setError('New password does not meet requirements. Use at least 8 characters with uppercase, lowercase, numbers, and symbols.');
      } else if (err.name === 'LimitExceededException') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(err.message || 'Failed to change password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="change-password-page">
      <div className="change-password-container">
        <div className="change-password-header">
          <h1 className="change-password-title">Change Password</h1>
          <p className="change-password-subtitle">
            Update your password to keep your account secure
          </p>
        </div>

        <form className="change-password-form" onSubmit={handleSubmit}>
          {error && <div className="change-password-error">{error}</div>}
          {successMessage && <div className="change-password-success">{successMessage}</div>}

          <Input
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter your current password"
            fullWidth
            required
            showPasswordToggle
          />

          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password (min. 8 characters)"
            fullWidth
            required
            showPasswordToggle
          />

          <Input
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your new password"
            fullWidth
            required
            showPasswordToggle
          />

          <div className="change-password-actions">
            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={isLoading}
            >
              Change Password
            </Button>
          </div>
        </form>

        <div className="change-password-footer">
          <Link to="/profile" className="change-password-link">
            ← Back to Profile
          </Link>
        </div>
      </div>
    </div>
  );
};
